import type { GeoTIFF, GeoTIFFImage } from 'geotiff';
import type { BandProfile, Profile, Sampling, Warning } from './types';
import { COMPRESSION, PHOTOMETRIC, SAMPLEFMT, NM } from './constants';
import { bandStats, detectSentinel } from './stats';
import { guessBands } from './bands';
import { classifyDomain } from './domain';

export interface ProbeOptions { url?: string; onProgress?: (msg: string) => void }

/**
 * Read only what is needed to understand a COG: headers, the smallest overview, and
 * one small full-resolution window. A few hundred KB regardless of file size.
 */
export async function probe(tiff: GeoTIFF, opts: ProbeOptions = {}): Promise<Profile> {
  const { url = '', onProgress = () => {} } = opts;
  onProgress('reading image file directories');

  const count = await tiff.getImageCount();
  const all: { img: GeoTIFFImage; i: number; isMask: boolean; w: number; h: number }[] = [];
  for (let i = 0; i < count; i++) {
    const img = await tiff.getImage(i);
    const fd = img.fileDirectory as unknown as Record<string, unknown>;
    const sub = (fd['NewSubfileType'] as number) ?? 0;
    const isMask = (sub & 0x4) !== 0 || fd['PhotometricInterpretation'] === 4;
    all.push({ img, i, isMask, w: img.getWidth(), h: img.getHeight() });
  }
  const pyramid = all.filter((x) => !x.isMask).sort((a, b) => b.w - a.w);
  if (!pyramid.length) throw new Error('No image data found — every IFD is a transparency mask.');
  const full = pyramid[0]!.img;
  const smallest = pyramid[pyramid.length - 1]!;
  const fd = full.fileDirectory as unknown as Record<string, any>;

  const samples = full.getSamplesPerPixel();
  const bitsArr = Array.from<number>(fd['BitsPerSample'] ?? [8]);
  const bits = bitsArr[0] ?? 8;
  const sampleFormat = Array.from<number>(fd['SampleFormat'] ?? [1])[0] ?? 1;
  const extraSamples = Array.from<number>(fd['ExtraSamples'] ?? []);
  const photometric = fd['PhotometricInterpretation'] as number | undefined;
  const tiled = fd['TileWidth'] !== undefined;
  const nodata = full.getGDALNoData();
  const warnings: Warning[] = [];

  // ExtraSamples describes the trailing samples only.
  const alphaFlags = new Array<boolean>(samples).fill(false);
  extraSamples.forEach((v, k) => {
    const bi = samples - extraSamples.length + k;
    if (v === 1 || v === 2) alphaFlags[bi] = true;
  });

  const perBandMeta: { description: string | null; scale: number | null; offset: number | null }[] = [];
  for (let b = 0; b < samples; b++) {
    let md: Record<string, string> = {};
    // Pinned to geotiff 2.x, where this is synchronous. It returns a Promise from 3.0
    // onward, and an un-awaited Promise reads as {} — every band description silently null.
    try { md = (full.getGDALMetadata(b) ?? {}) as unknown as Record<string, string>; } catch { /* none */ }
    perBandMeta.push({
      description: md['DESCRIPTION'] ?? null,
      scale: md['SCALE'] != null ? +md['SCALE'] : null,
      offset: md['OFFSET'] != null ? +md['OFFSET'] : null,
    });
  }

  if (!tiled) warnings.push({ level: 'bad', text: '**Not tiled.** This is a stripped TIFF, not a COG. A viewer cannot fetch sub-regions, so every pan and zoom pulls the whole file.' });
  if (pyramid.length < 2) warnings.push({ level: 'bad', text: '**No overviews.** Zoomed-out views must decode full resolution. Rebuild with `gdal_translate -of COG`.' });

  // The smallest overview is one range request and representative of the whole scene.
  const px = smallest.w * smallest.h;
  const bytes = px * samples * (bits / 8);
  const tooBig = px > 700_000 || bytes > 8_000_000 || (pyramid.length === 1 && px > 262_144);
  let read: Record<string, unknown>;
  let sampling: Sampling;
  if (!tooBig) {
    onProgress(`reading overview ${smallest.w}×${smallest.h}`);
    read = { width: smallest.w, height: smallest.h };
    sampling = { kind: 'overview', width: smallest.w, height: smallest.h,
      note: 'Statistics come from the smallest overview — a downsample of the entire scene.' };
  } else {
    const W = 1024;
    const x0 = Math.max(0, Math.floor(smallest.w / 2 - W / 2));
    const y0 = Math.max(0, Math.floor(smallest.h / 2 - W / 2));
    const win = [x0, y0, Math.min(smallest.w, x0 + W), Math.min(smallest.h, y0 + W)];
    onProgress(`no small overview — reading centre ${W}×${W} window`);
    read = { window: win, width: 256, height: 256 };
    sampling = { kind: 'window', width: 256, height: 256, window: win,
      note: 'No small overview available, so statistics come from a centre window only.' };
    warnings.push({ level: 'warn', text: 'Statistics are from a **centre window**, not the full scene, because the file has no small overview.' });
  }

  // GDAL builds a mask pyramid alongside the data pyramid, so there is normally a mask
  // IFD at exactly the size we sampled. An internal mask carries no nodata value.
  let validMask: Uint8Array | null = null;
  const maskIFD = all.filter((x) => x.isMask).find((m) => m.w === smallest.w && m.h === smallest.h);
  if (maskIFD) {
    try {
      onProgress('reading internal mask');
      const mr = await maskIFD.img.readRasters(read as never);
      const plane = (Array.isArray(mr) ? mr[0] : mr) as ArrayLike<number>;
      validMask = new Uint8Array(plane.length);
      for (let i = 0; i < plane.length; i++) validMask[i] = plane[i]! > 0 ? 1 : 0;
    } catch { validMask = null; }
  }

  let raw: unknown;
  try { raw = await smallest.img.readRasters(read as never); } catch (e) {
    throw new Error(`Pixel decode failed (${COMPRESSION[fd['Compression'] as number] ?? 'compression ' + fd['Compression']}): ${(e as Error).message}. geotiff.js does not decode every codec — JPEG-in-TIFF with YCbCr, ZSTD and LERC need extra decoders.`);
  }
  const planes = (Array.isArray(raw) ? raw : [raw]) as Profile['planes'];
  const width = (raw as { width?: number }).width ?? (read['width'] as number);
  const height = (raw as { height?: number }).height ?? (read['height'] as number);

  onProgress('computing statistics');
  let stats = planes.map((p) => bandStats(p as ArrayLike<number>, nodata, validMask));
  const sentinel = validMask ? null : detectSentinel(stats[0]!, nodata);
  if (sentinel != null) {
    stats = planes.map((p) => bandStats(p as ArrayLike<number>, sentinel, validMask));
    warnings.push({ level: 'warn', text: `Found ${sentinel} in the data but **no nodata value is declared** in the file. Treated as nodata for statistics.` });
  }

  // Overview pixels are averages, so the saturation ceiling that separates 12-bit DN
  // from reflectance×10000 is smoothed away. One small full-resolution window puts it back.
  let ceiling: number | null = null;
  if (sampleFormat !== 3 && bits > 8 && sampling.kind === 'overview') {
    try {
      onProgress('checking full-resolution saturation ceiling');
      const W = 256, fw = full.getWidth(), fh = full.getHeight();
      const x0 = Math.max(0, Math.floor(fw / 2 - W / 2));
      const y0 = Math.max(0, Math.floor(fh / 2 - W / 2));
      const probeWin = await full.readRasters({ window: [x0, y0, Math.min(fw, x0 + W), Math.min(fh, y0 + W)] } as never);
      const pl = (Array.isArray(probeWin) ? probeWin : [probeWin]) as ArrayLike<number>[];
      let m = -Infinity;
      pl.forEach((a, bi) => {
        if (alphaFlags[bi]) return;
        for (let i = 0; i < a.length; i++) {
          const v = a[i]!;
          if (Number.isFinite(v) && (nodata == null || v !== nodata) && v > m) m = v;
        }
      });
      ceiling = isFinite(m) ? m : null;
    } catch { ceiling = null; }
  }

  const { bands: roles, notes } = guessBands({
    samples, photometric, alphaFlags,
    descriptions: perBandMeta.map((m) => m.description), sampleFormat, stats,
  });

  const bands: BandProfile[] = roles.map((r, i) => ({
    index: i, glslIndex: i + 1, role: r.role, source: r.source, why: r.why,
    description: perBandMeta[i]!.description, scale: perBandMeta[i]!.scale,
    offset: perBandMeta[i]!.offset, wavelengthNm: NM[r.role] ?? null,
    isAlpha: alphaFlags[i] ?? false, stats: stats[i]!,
  }));

  const domain = classifyDomain({ sampleFormat, bits, bands, perBandMeta, ceiling });

  let epsg: number | null = null;
  try {
    const gk = ((full as unknown as { geoKeys?: Record<string, number> }).geoKeys ?? {});
    epsg = gk['ProjectedCSTypeGeoKey'] ?? gk['GeographicTypeGeoKey'] ?? null;
  } catch { /* none */ }
  let bbox: number[] | null = null, res: number[] | null = null;
  try { bbox = full.getBoundingBox(); res = full.getResolution(); } catch { /* none */ }

  return {
    cogProbeVersion: 1, url, probedAt: new Date().toISOString(),
    raster: {
      width: full.getWidth(), height: full.getHeight(), samplesPerPixel: samples,
      bitsPerSample: bitsArr, sampleFormat, sampleFormatName: SAMPLEFMT[sampleFormat] ?? '?',
      compression: fd['Compression'], compressionName: COMPRESSION[fd['Compression'] as number] ?? `code ${fd['Compression']}`,
      photometric, photometricName: PHOTOMETRIC[photometric as number] ?? `code ${photometric}`,
      planarConfiguration: fd['PlanarConfiguration'] ?? 1,
      tiled, tileWidth: fd['TileWidth'] ?? null, tileHeight: fd['TileLength'] ?? null,
      isCOG: tiled && pyramid.length > 1,
      overviews: pyramid.map((p, i) => ({ level: i, width: p.w, height: p.h, ifd: p.i })),
      maskIFDs: all.filter((x) => x.isMask).length,
      epsg, bbox, resolution: res, nodata: nodata ?? sentinel ?? null,
      nodataDeclared: nodata != null, fullResCeiling: ceiling, maskApplied: !!validMask,
    },
    domain, bands, sampling, warnings, bandNotes: notes,
    planes, sampleSize: { width, height },
    pyramid: pyramid.map((x) => ({ image: x.img, width: x.w, height: x.h })),
  };
}
